import React, { useMemo } from 'react';
import './EmployeeMarketComparisonList.css';
import { LocalEmployee } from '../../services/localEmployeesStore';
import type {
  EmployeeGradingOverride,
  PositionGrading,
} from '../../services/localEmployeeGradingStore';
import { MarketDataRecord } from '../../services/marketDataService';
import type { RoleClusterMeta } from '../../utils/marketClusterMeta';
import {
  buildMarketBenchmarksByGroup,
  employeeBenchmarkLabel,
  employeeGradingSubtitle,
  employeeMarketGroupKey,
  resolveComparisonGroupingMode,
  resolveEmployeeGrading,
  type MarketBenchmark,
} from '../../utils/employeeMarketComparisonUtils';
import {
  getSortedMarketGroups,
  type MarketGroupingMode,
} from '../../utils/marketGroupUtils';
import MarketDashboardGroupingSwitch from './MarketDashboardGroupingSwitch';

type Props = {
  employees: LocalEmployee[];
  filteredMarketData: MarketDataRecord[];
  positionGradings: PositionGrading[];
  employeeGradingOverrides: EmployeeGradingOverride[];
  groupingMode: MarketGroupingMode;
  onGroupingModeChange: (mode: MarketGroupingMode) => void;
  clusterMetas: RoleClusterMeta[];
  clusterGroupingReady: boolean;
};

function clamp01(x: number) {
  if (!Number.isFinite(x)) return 0;
  return Math.max(0, Math.min(1, x));
}

function toPct(pos01: number) {
  return `${(pos01 * 100).toFixed(2)}%`;
}

function calculatePercentileSorted(sorted: number[], percentile: number): number {
  if (sorted.length === 0) return 0;
  const index = Math.ceil((percentile / 100) * sorted.length) - 1;
  return sorted[Math.max(0, index)];
}

function upperBound(sorted: number[], x: number): number {
  let lo = 0;
  let hi = sorted.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (sorted[mid] <= x) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

function rankPercentileSorted(sorted: number[], x: number): number | null {
  if (sorted.length === 0) return null;
  const le = upperBound(sorted, x);
  return (le / sorted.length) * 100;
}

function initials(fullName: string) {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  const a = (parts[0]?.[0] ?? '?').toUpperCase();
  const b = (parts[1]?.[0] ?? '').toUpperCase();
  return (a + b).slice(0, 2);
}

function formatRub(v: number): string {
  return new Intl.NumberFormat('ru-RU').format(Math.round(v));
}

function formatStiPct(v: number): string {
  return `${(v * 100).toFixed(1)}%`;
}

type MiniBarPercentiles = {
  p10: number;
  p25: number;
  p50: number;
  p75: number;
  p90: number;
};

type PercentileMiniBarProps = {
  percentiles: MiniBarPercentiles;
  formatVal: (v: number) => string;
  marker01: number;
  tooltipLine: string;
};

const P25_P75_VISUAL_INSET = 0.022;

function PercentileMiniBar({ percentiles, formatVal, marker01, tooltipLine }: PercentileMiniBarProps) {
  const { p10, p25, p50, p75, p90 } = percentiles;
  const iqrVisualLeft = 0.25 + P25_P75_VISUAL_INSET;
  const iqrVisualWidth = 0.5 - 2 * P25_P75_VISUAL_INSET;
  const ticks: { visualPos: number; key: string; short: string }[] = [
    { visualPos: 0.1, key: 'p10', short: 'p10' },
    { visualPos: 0.25 + P25_P75_VISUAL_INSET, key: 'p25', short: 'p25' },
    { visualPos: 0.75 - P25_P75_VISUAL_INSET, key: 'p75', short: 'p75' },
    { visualPos: 0.9, key: 'p90', short: 'p90' },
  ];
  const tickValues = [p10, p25, p75, p90];

  const pctLabels: { visualPos: number; label: string }[] = [
    { visualPos: 0.1, label: 'p10' },
    { visualPos: 0.25 + P25_P75_VISUAL_INSET, label: 'p25' },
    { visualPos: 0.5, label: 'M' },
    { visualPos: 0.75 - P25_P75_VISUAL_INSET, label: 'p75' },
    { visualPos: 0.9, label: 'p90' },
  ];

  return (
    <div className="mini-bar-track-wrap">
      <div className="mini-bar-viz">
        <div className="mini-bar-track-row">
          <div className="mini-bar-track">
            <div
              className="mini-bar-iqr"
              style={{ left: toPct(iqrVisualLeft), width: toPct(iqrVisualWidth) }}
              title={`IQR: ${formatVal(p25)} – ${formatVal(p75)}`}
            />
            <div className="mini-bar-ticks-ink" aria-hidden="true">
              <div className="mini-bar-median-ink" style={{ left: toPct(0.5) }} />
              {ticks.map((t) => (
                <div key={`ink-${t.key}`} className="mini-bar-pct-ink" style={{ left: toPct(t.visualPos) }}>
                  <span className="mini-bar-pct-ink-line" />
                </div>
              ))}
            </div>
          </div>
          <div className="mini-bar-interaction-layer">
            <div className="mini-bar-median-hit" style={{ left: toPct(0.5) }}>
              <div className="mini-bar-tooltip mini-bar-tooltip--pct" aria-hidden="true">
                M (p50): {formatVal(p50)}
              </div>
            </div>
            {ticks.map((t, i) => (
              <div key={t.key} className="mini-bar-pct-hit" style={{ left: toPct(t.visualPos) }}>
                <div className="mini-bar-tooltip mini-bar-tooltip--pct" aria-hidden="true">
                  {t.short}: {formatVal(tickValues[i])}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mini-bar-pct-labels" aria-hidden="true">
          {pctLabels.map((row) => (
            <div key={row.label} className="mini-bar-pct-label" style={{ left: toPct(row.visualPos) }}>
              <div className="mini-bar-pct-name">{row.label}</div>
            </div>
          ))}
        </div>

        <div className="mini-bar-marker-wrap" style={{ left: `calc(${toPct(marker01)} - 9px)` }}>
          <div className="mini-bar-marker" aria-hidden="true" />
          <div className="mini-bar-tooltip" aria-hidden="true">
            {tooltipLine}
          </div>
        </div>
      </div>
    </div>
  );
}

function renderBenchmarkBars(
  market: MarketBenchmark | null,
  emp: LocalEmployee,
  benchmarkLabel: string,
) {
  const fixSorted = market?.fix ?? [];
  const stiSorted = market?.sti ?? [];
  const totalSorted = market?.total ?? [];
  const shareYes = market && market.n > 0 ? (market.yes / market.n) * 100 : null;

  const fixP10 = fixSorted.length ? calculatePercentileSorted(fixSorted, 10) : null;
  const fixP25 = fixSorted.length ? calculatePercentileSorted(fixSorted, 25) : null;
  const fixP50 = fixSorted.length ? calculatePercentileSorted(fixSorted, 50) : null;
  const fixP75 = fixSorted.length ? calculatePercentileSorted(fixSorted, 75) : null;
  const fixP90 = fixSorted.length ? calculatePercentileSorted(fixSorted, 90) : null;
  const fixPct = rankPercentileSorted(fixSorted, emp.fixMonthlyGross);

  const stiP10 = stiSorted.length ? calculatePercentileSorted(stiSorted, 10) : null;
  const stiP25 = stiSorted.length ? calculatePercentileSorted(stiSorted, 25) : null;
  const stiP50 = stiSorted.length ? calculatePercentileSorted(stiSorted, 50) : null;
  const stiP75 = stiSorted.length ? calculatePercentileSorted(stiSorted, 75) : null;
  const stiP90 = stiSorted.length ? calculatePercentileSorted(stiSorted, 90) : null;
  const stiPct = rankPercentileSorted(stiSorted, emp.stiFraction);

  const empTotalComp = emp.fixMonthlyGross * 12 * (1 + emp.stiFraction);
  const totalP10 = totalSorted.length ? calculatePercentileSorted(totalSorted, 10) : null;
  const totalP25 = totalSorted.length ? calculatePercentileSorted(totalSorted, 25) : null;
  const totalP50 = totalSorted.length ? calculatePercentileSorted(totalSorted, 50) : null;
  const totalP75 = totalSorted.length ? calculatePercentileSorted(totalSorted, 75) : null;
  const totalP90 = totalSorted.length ? calculatePercentileSorted(totalSorted, 90) : null;
  const totalPct = rankPercentileSorted(totalSorted, empTotalComp);

  const fixMarker = clamp01((fixPct ?? 0) / 100);
  const stiMarker = clamp01((stiPct ?? 0) / 100);
  const totalMarker = clamp01((totalPct ?? 0) / 100);
  const benchmarkHint = market && market.n > 0 ? `Бенчмарк: ${benchmarkLabel} · n=${market.n}` : null;

  return (
    <>
      <div className="mini-bar-wrap">
        <div className="mini-bar-title">FIX (₽/мес)</div>
        {benchmarkHint && <div className="emp-benchmark-hint">{benchmarkHint}</div>}
        {fixP10 === null ||
        fixP25 === null ||
        fixP50 === null ||
        fixP75 === null ||
        fixP90 === null ? (
          <div className="ltip-muted">Нет данных рынка</div>
        ) : (
          <PercentileMiniBar
            percentiles={{ p10: fixP10, p25: fixP25, p50: fixP50, p75: fixP75, p90: fixP90 }}
            formatVal={(v) => `${formatRub(v)} ₽`}
            marker01={fixMarker}
            tooltipLine={`FIX: ${formatRub(emp.fixMonthlyGross)} ₽ • ~${(fixPct ?? 0).toFixed(0)}p`}
          />
        )}
      </div>

      <div className="mini-bar-wrap">
        <div className="mini-bar-title">STI</div>
        {stiP10 === null ||
        stiP25 === null ||
        stiP50 === null ||
        stiP75 === null ||
        stiP90 === null ? (
          <div className="ltip-muted">Нет данных рынка</div>
        ) : (
          <PercentileMiniBar
            percentiles={{ p10: stiP10, p25: stiP25, p50: stiP50, p75: stiP75, p90: stiP90 }}
            formatVal={formatStiPct}
            marker01={stiMarker}
            tooltipLine={`STI: ${formatStiPct(emp.stiFraction)} • ~${(stiPct ?? 0).toFixed(0)}p`}
          />
        )}
      </div>

      <div className="mini-bar-wrap">
        <div className="mini-bar-title">Total compensation (₽/год)</div>
        {totalP10 === null ||
        totalP25 === null ||
        totalP50 === null ||
        totalP75 === null ||
        totalP90 === null ? (
          <div className="ltip-muted">Нет данных рынка</div>
        ) : (
          <PercentileMiniBar
            percentiles={{
              p10: totalP10,
              p25: totalP25,
              p50: totalP50,
              p75: totalP75,
              p90: totalP90,
            }}
            formatVal={(v) => `${formatRub(v)} ₽`}
            marker01={totalMarker}
            tooltipLine={`Total: ${formatRub(empTotalComp)} ₽ • ~${(totalPct ?? 0).toFixed(0)}p`}
          />
        )}
      </div>

      <div className="ltip-box">
        <div className="ltip-line">
          У сотрудника: <strong>{emp.ltip ? 'Да' : 'Нет'}</strong>
        </div>
        <div className="ltip-muted">
          Доля с LTIP:{' '}
          <strong>{shareYes === null ? '—' : `${shareYes.toFixed(0)}%`}</strong>
        </div>
      </div>
    </>
  );
}

export const EmployeeMarketComparisonList: React.FC<Props> = ({
  employees,
  filteredMarketData,
  positionGradings,
  employeeGradingOverrides,
  groupingMode,
  onGroupingModeChange,
  clusterMetas,
  clusterGroupingReady,
}) => {
  const effectiveMode = resolveComparisonGroupingMode(groupingMode, clusterMetas);

  const { benchmarksByKey, groupByKey } = useMemo(() => {
    const benchmarksByKey = buildMarketBenchmarksByGroup(
      filteredMarketData,
      effectiveMode,
      clusterMetas,
    );
    const groups = getSortedMarketGroups(
      filteredMarketData.filter((record) => record.family_name && record.grade_level != null),
      effectiveMode,
      clusterMetas,
    );
    const groupByKey = new Map(groups.map((group) => [group.key, group]));
    return { benchmarksByKey, groupByKey };
  }, [filteredMarketData, effectiveMode, clusterMetas]);

  if (employees.length === 0) {
    return <div style={{ color: '#666' }}>Нет сотрудников для сравнения.</div>;
  }

  return (
    <div className="emp-comparison-block">
      <div className="emp-comparison-toolbar">
        <MarketDashboardGroupingSwitch
          mode={effectiveMode}
          onChange={onGroupingModeChange}
          disabled={!clusterGroupingReady && groupingMode === 'cluster'}
        />
        <div className="emp-comparison-toolbar-hint">
          {effectiveMode === 'cluster'
            ? 'Сравнение с рынком по кластеру: объединяются записи семейства с грейдами из диапазона кластера.'
            : 'Сравнение с рынком по роли: family + grade level из грейдирования.'}
        </div>
      </div>

      <div className="emp-list" role="table" aria-label="Сравнение сотрудников с рынком">
        <div className="emp-row emp-row-head" role="row">
          <div>Сотрудник</div>
          <div>FIX</div>
          <div>FIX vs рынок</div>
          <div>STI vs рынок</div>
          <div>Total vs рынок</div>
          <div style={{ textAlign: 'right' }}>LTIP</div>
        </div>

        {employees.map((emp) => {
          const grading = resolveEmployeeGrading(
            emp,
            positionGradings,
            employeeGradingOverrides,
          );

          if (!grading) {
            return (
              <div className="emp-row emp-row-ungraded" role="row" key={emp.id}>
                <div className="emp-person">
                  <div className="emp-avatar" aria-hidden="true">{initials(emp.fullName)}</div>
                  <div className="emp-namepos">
                    <div className="emp-name">{emp.fullName}</div>
                    <div className="emp-pos">{emp.position}</div>
                    <div className="emp-grade-warning">
                      Грейд не задан — укажите на вкладке «Грейдирование»
                    </div>
                  </div>
                </div>
                <div className="emp-value">{formatRub(emp.fixMonthlyGross)} ₽</div>
                <div className="emp-ungraded-placeholder" style={{ gridColumn: 'span 4' }}>
                  Сравнение недоступно без грейда
                </div>
              </div>
            );
          }

          const groupKey = employeeMarketGroupKey(grading, effectiveMode);
          const market = benchmarksByKey.get(groupKey) ?? null;
          const group = groupByKey.get(groupKey);
          const benchmarkLabel = employeeBenchmarkLabel(grading, effectiveMode, group);
          const subtitle = employeeGradingSubtitle(grading, effectiveMode);

          return (
            <div className="emp-row" role="row" key={emp.id}>
              <div className="emp-person">
                <div className="emp-avatar" aria-hidden="true">{initials(emp.fullName)}</div>
                <div className="emp-namepos">
                  <div className="emp-name">{emp.fullName}</div>
                  <div className="emp-pos">{emp.position}</div>
                  <div className="emp-grade-line">{subtitle}</div>
                  {grading.source === 'employee' && (
                    <div className="emp-grade-override-badge">Индивидуальный грейд</div>
                  )}
                </div>
              </div>

              <div className="emp-value">{formatRub(emp.fixMonthlyGross)} ₽</div>

              {market && market.n > 0 ? (
                renderBenchmarkBars(market, emp, benchmarkLabel)
              ) : (
                <>
                  <div className="mini-bar-wrap">
                    <div className="mini-bar-title">FIX (₽/мес)</div>
                    <div className="ltip-muted">Нет данных рынка для {benchmarkLabel}</div>
                  </div>
                  <div className="mini-bar-wrap">
                    <div className="mini-bar-title">STI</div>
                    <div className="ltip-muted">—</div>
                  </div>
                  <div className="mini-bar-wrap">
                    <div className="mini-bar-title">Total compensation (₽/год)</div>
                    <div className="ltip-muted">—</div>
                  </div>
                  <div className="ltip-box">
                    <div className="ltip-line">
                      У сотрудника: <strong>{emp.ltip ? 'Да' : 'Нет'}</strong>
                    </div>
                    <div className="ltip-muted">Доля с LTIP: <strong>—</strong></div>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
